/**
 * The account page: who is signed in, and what they have chosen.
 *
 * Who is signed in is the header (name, email, role), said once; an
 * account card beside it repeated all three. Then how the site looks, the
 * choices remembered, invitations for the owner, and signing out last.
 *
 * Deliberately short. It shows what the platform actually knows about the
 * account and what this side of it remembers, and nothing invented to fill
 * the page -- a profile with an empty "activity" panel on it is worse than
 * one that admits there is nothing to show.
 */

import { Check, Copy, LogOut, Palette, SlidersHorizontal, UserPlus } from "lucide-react";
import { useCallback, useState } from "react";

import type { Account } from "@/api/client";
import { createInvitation, fetchScopes } from "@/api/client";
import { OVERLAYS } from "@/components/ChartControls";
import { Chooser } from "@/components/Chooser";
import { PageHeader } from "@/components/PageHeader";
import { PRICE_RANGES } from "@/lib/priceRanges";
import { ScopePicker } from "@/components/ScopePicker";
import { useResource } from "@/hooks/useResource";
import { resetPreferences, usePreferences, writePreferences } from "@/lib/preferences";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { THEME_CHOICES } from "@/components/ThemeMenu";
import { useTheme } from "@/lib/theme";
import { sentence } from "@/lib/format";

interface ProfileProps {
  account: Account;
  onSignOut: () => void;
}

/**
 * Render the profile page.
 *
 * @param props - Who is signed in, and how to end the session.
 * @returns The page.
 */
export function Profile({ account, onSignOut }: ProfileProps): React.JSX.Element {
  const { choice, appearance, setChoice } = useTheme();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title={account.display_name}
        identifiers={account.email}
        badges={<Badge variant="outline">{account.is_owner ? "Owner" : "Member"}</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette aria-hidden="true" className="h-4 w-4 text-primary" />
            Appearance
          </CardTitle>
          <CardDescription>
            Remembered in this browser, not on the account: a different machine starts from its own
            setting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <p className="text-sm font-medium">Light and dark</p>
            <Chooser
              options={THEME_CHOICES.map(({ choice: key, label, icon }) => ({ key, label, icon }))}
              chosen={choice}
              onChange={setChoice}
              label="Appearance"
            />
            <p className="text-xs text-muted-foreground">
              {choice === "system"
                ? `Following this machine, which is currently ${appearance}.`
                : `Fixed to ${choice}, whatever the machine is set to.`}
            </p>
          </div>
        </CardContent>
      </Card>

      <PreferencesCard />
      {account.is_owner && <InviteCard />}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LogOut aria-hidden="true" className="h-4 w-4 text-primary" />
            Session
          </CardTitle>
          <CardDescription>
            Signing out ends this session on the platform, not only in this browser.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={onSignOut}>
            <LogOut aria-hidden="true" className="h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/** The choices the reader makes once and should not make again. */
function PreferencesCard(): React.JSX.Element {
  const preferences = usePreferences();
  const loadScopes = useCallback(() => fetchScopes(), []);
  const scopes = useResource(loadScopes);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-primary" />
          Preferences
        </CardTitle>
        <CardDescription>
          The population the overview opens on, how a price chart is drawn, and how far back a chart
          reaches. Remembered by this browser; changing any of them on a page remembers it too.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Default population</h3>
          <ScopePicker
            scope={preferences.scope}
            options={scopes.data}
            onChange={(next) => {
              writePreferences({ scope: next });
            }}
          />
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Price chart</h3>
          <Chooser
            options={[
              { key: "line", label: "Line" },
              { key: "candles", label: "Candles" },
              { key: "area", label: "Area" },
            ]}
            chosen={preferences.chartStyle}
            onChange={(next) => {
              writePreferences({ chartStyle: next });
            }}
            label="Chart style"
          />
          <div className="flex flex-wrap gap-3" role="group" aria-label="Overlays">
            {OVERLAYS.map((overlay) => (
              <label key={overlay.key} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={preferences.overlays.includes(overlay.key)}
                  onChange={(event) => {
                    writePreferences({
                      overlays: event.target.checked
                        ? [...preferences.overlays, overlay.key]
                        : preferences.overlays.filter((one) => one !== overlay.key),
                    });
                  }}
                />
                {overlay.label}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Default range</h3>
          <Chooser
            options={PRICE_RANGES.map((range) => ({
              key: String(range.sessions),
              label: range.label,
            }))}
            chosen={String(preferences.range)}
            onChange={(next) => {
              writePreferences({ range: Number(next) });
            }}
            label="Default range"
          />
        </div>
        <Button variant="outline" size="sm" onClick={resetPreferences}>
          Forget my preferences
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Mint an invitation link for a friend. Owner only.
 *
 * The link opens the sign-up form with the code filled in. The code is
 * single-use and shown only here, once: the platform keeps its fingerprint,
 * so a lost link is replaced by minting another.
 */
function InviteCard(): React.JSX.Element {
  const [days, setDays] = useState("7");
  const [link, setLink] = useState<{ url: string; expires: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mint = (): void => {
    setBusy(true);
    setProblem(null);
    setCopied(false);
    createInvitation(Number(days))
      .then((invitation) => {
        setLink({
          url: `${window.location.origin}/?invite=${encodeURIComponent(invitation.code)}`,
          expires: new Date(invitation.expires_at).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
        });
      })
      .catch((error: unknown) => {
        setProblem(error instanceof Error ? error.message : "could not create an invitation");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const copy = (url: string): void => {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
      },
      () => {
        setProblem("the browser refused the clipboard; select the link and copy it");
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPlus aria-hidden="true" className="h-4 w-4 text-primary" />
          Invite a friend
        </CardTitle>
        <CardDescription>
          There is no open sign-up. Make a link, send it to one person; it creates one account and
          then stops working.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Chooser
            options={[
              { key: "1", label: "1 day" },
              { key: "7", label: "7 days" },
              { key: "30", label: "30 days" },
            ]}
            chosen={days}
            onChange={setDays}
            label="Valid for"
          />
          <Button size="sm" disabled={busy} onClick={mint}>
            {busy ? "Creating…" : "Create invitation link"}
          </Button>
        </div>
        {link !== null && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <code
                className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs"
                aria-label="Invitation link"
              >
                {link.url}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  copy(link.url);
                }}
              >
                {copied ? (
                  <Check aria-hidden="true" className="mr-1.5 h-4 w-4" />
                ) : (
                  <Copy aria-hidden="true" className="mr-1.5 h-4 w-4" />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Works once, until {link.expires}. It is shown only now.
            </p>
          </div>
        )}
        {problem !== null && <p className="text-sm text-destructive">{sentence(problem)}</p>}
      </CardContent>
    </Card>
  );
}
